import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import {
  getPendingLanguages, getPendingEducations, getPendingWorkExperiences,
  approveLanguage, rejectLanguage, approveEducation, rejectEducation,
  approveWorkExperience, rejectWorkExperience,
} from '../../services/resumeService';
import { formatDate } from '../../utils/Utils';
import Tabs from '../../components/ui/Tabs';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PersonCell from '../../components/ui/PersonCell';
import Tooltip from '../../components/ui/Tooltip';
import TextInput from '../../components/ui/TextInput.jsx';

const Container = styled.div``;

const PageTitle = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  gap: 16px;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const OwnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const OwnerMeta = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
  &::before {
    content: '·';
    margin-right: 8px;
    color: #cbd5e1;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #F1F4F7;
  margin: 4px 0;
`;

const CardTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardSub = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 4px;
  border: 1px solid ${({ $variant }) => $variant === 'approve' ? '#10b981' : '#fecaca'};
  background: ${({ $variant }) => $variant === 'approve' ? '#10b981' : '#fff'};
  color: ${({ $variant }) => $variant === 'approve' ? '#fff' : '#ef4444'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    ${({ $variant }) => $variant === 'approve'
      ? 'background: #059669; border-color: #059669;'
      : 'background: #ef4444; color: #fff;'}
  }
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

export default function ResumePendingPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('languages');
  const [languages, setLanguages] = useState([]);
  const [educations, setEducations] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reject dialog
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, type: null, id: null, name: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [langRes, eduRes, expRes] = await Promise.all([
        getPendingLanguages({ pageSize: 100 }),
        getPendingEducations({ pageSize: 100 }),
        getPendingWorkExperiences({ pageSize: 100 }),
      ]);
      setLanguages(langRes.data?.data?.data || langRes.data?.data || []);
      setEducations(eduRes.data?.data?.data || eduRes.data?.data || []);
      setWorkExperiences(expRes.data?.data?.data || expRes.data?.data || []);
    } catch { /* interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApprove = useCallback(async (type, id) => {
    try {
      if (type === 'language') await approveLanguage(id);
      else if (type === 'education') await approveEducation(id);
      else if (type === 'workExperience') await approveWorkExperience(id);
      await fetchAll();
    } catch { /* interceptor */ }
  }, [fetchAll]);

  const handleRejectClick = useCallback((type, item) => {
    setRejectDialog({
      isOpen: true,
      type,
      id: item.id,
      name: item.language || item.institution || item.title || '',
    });
    setRejectReason('');
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    setRejecting(true);
    try {
      if (rejectDialog.type === 'language') await rejectLanguage(rejectDialog.id, rejectReason);
      else if (rejectDialog.type === 'education') await rejectEducation(rejectDialog.id, rejectReason);
      else if (rejectDialog.type === 'workExperience') await rejectWorkExperience(rejectDialog.id, rejectReason);
      await fetchAll();
    } catch { /* interceptor */ }
    setRejectDialog({ isOpen: false, type: null, id: null, name: '' });
    setRejecting(false);
  }, [rejectDialog, rejectReason, fetchAll]);

  const tabs = useMemo(() => [
    { key: 'languages', label: t('resume.languages') },
    { key: 'education', label: t('resume.education') },
    { key: 'workExperience', label: t('resume.workExperience') },
  ], [t]);

  // The owner header is identical across all three card types. Extracting it keeps
  // the per-type renderers focused on their unique fields.
  const renderOwnerHeader = (item) => (
    <>
      <OwnerRow>
        {item.userFullName && (
          <PersonCell name={item.userFullName} profilePicture={item.userProfilePicture} />
        )}
        {item.userJobTitle && <OwnerMeta>{item.userJobTitle}</OwnerMeta>}
        {item.userDepartmentName && <OwnerMeta>{item.userDepartmentName}</OwnerMeta>}
      </OwnerRow>
      <Divider />
    </>
  );

  const renderActions = (type, item) => (
    <CardActions>
      <Tooltip text={t('resume.approve')}>
        <IconBtn $variant="approve" onClick={() => handleApprove(type, item.id)} aria-label={t('resume.approve')}>
          <Check size={18} />
        </IconBtn>
      </Tooltip>
      <Tooltip text={t('resume.reject')}>
        <IconBtn onClick={() => handleRejectClick(type, item)} aria-label={t('resume.reject')}>
          <X size={18} />
        </IconBtn>
      </Tooltip>
    </CardActions>
  );

  const renderLanguageCard = (item) => (
    <Card key={item.id}>
      <CardInfo>
        {renderOwnerHeader(item)}
        <CardTitle>{item.language}</CardTitle>
        <CardSub>
          {item.isNative
            ? t('resume.native')
            : `${t('resume.spokenProficiency')}: ${item.spokenProficiency || '-'} | ${t('resume.writtenProficiency')}: ${item.writtenProficiency || '-'}`}
        </CardSub>
      </CardInfo>
      {renderActions('language', item)}
    </Card>
  );

  const renderEducationCard = (item) => (
    <Card key={item.id}>
      <CardInfo>
        {renderOwnerHeader(item)}
        <CardTitle>{item.institution}</CardTitle>
        <CardSub>
          {item.educationLevel}
          {item.faculty && ` — ${item.faculty}`}
          {item.graduationDate && ` | ${formatDate(item.graduationDate, i18n.language)}`}
        </CardSub>
      </CardInfo>
      {renderActions('education', item)}
    </Card>
  );

  const renderWorkExperienceCard = (item) => (
    <Card key={item.id}>
      <CardInfo>
        {renderOwnerHeader(item)}
        <CardTitle>{item.title}</CardTitle>
        <CardSub>
          {item.organization}
          {item.employmentDate && ` | ${formatDate(item.employmentDate, i18n.language)}`}
          {item.endDate && ` → ${formatDate(item.endDate, i18n.language)}`}
          {item.location && ` | ${item.location}`}
        </CardSub>
      </CardInfo>
      {renderActions('workExperience', item)}
    </Card>
  );

  const renderContent = () => {
    if (loading) return <EmptyText>{t('common.loading')}</EmptyText>;

    if (activeTab === 'languages') {
      return languages.length === 0
        ? <EmptyText>{t('resume.noLanguages')}</EmptyText>
        : <CardList>{languages.map(renderLanguageCard)}</CardList>;
    }
    if (activeTab === 'education') {
      return educations.length === 0
        ? <EmptyText>{t('resume.noEducations')}</EmptyText>
        : <CardList>{educations.map(renderEducationCard)}</CardList>;
    }
    if (activeTab === 'workExperience') {
      return workExperiences.length === 0
        ? <EmptyText>{t('resume.noExperiences')}</EmptyText>
        : <CardList>{workExperiences.map(renderWorkExperienceCard)}</CardList>;
    }
    return null;
  };

  return (
    <Container>
      <PageTitle>{t('resume.pending')}</PageTitle>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {renderContent()}

      <ConfirmDialog
        isOpen={rejectDialog.isOpen}
        title={t('resume.reject')}
        message={
          <div>
            <p style={{ marginBottom: 12 }}>{t('approvalFlow.deleteMessage', { name: rejectDialog.name })}</p>
            <TextInput
              label={t('resume.rejectReason')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        }
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectDialog({ isOpen: false, type: null, id: null, name: '' })}
        loading={rejecting}
      />
    </Container>
  );
}
